/**
 * Finexus Email Signature Generator
 * 
 * This application generates professional email signatures for Finexus employees.
 * Users fill in a form with their details, and the app generates two signatures:
 * 1. Full signature - for new emails (includes banner, social links, disclaimer)
 * 2. Reply signature - minimal version (just name and job title)
 * 
 * Configuration (colors, offices, banner) is in utils.ts
 */

import {
    SignatureData,
    isFormDataValid,
    getMissingFields,
    createSignatureHtml,
    createReplySignatureHtml
} from './utils';

/**
 * Main application class that handles:
 * - Form input and validation
 * - Live signature preview generation
 * - Copying signatures to clipboard (with Outlook-compatible formatting)
 */
class SignatureGenerator {
    private form: HTMLFormElement;
    private previewContainer: HTMLElement;
    private previewActions: HTMLElement;
    private replyPreviewContainer: HTMLElement;
    private replyPreviewActions: HTMLElement;

    constructor() {
        this.form = document.getElementById('signatureForm') as HTMLFormElement;
        this.previewContainer = document.getElementById('previewContainer') as HTMLElement;
        this.previewActions = document.getElementById('previewActions') as HTMLElement;
        this.replyPreviewContainer = document.getElementById('replyPreviewContainer') as HTMLElement;
        this.replyPreviewActions = document.getElementById('replyPreviewActions') as HTMLElement;

        this.initializeEventListeners();
    }

    private initializeEventListeners(): void {
        const copyHtmlBtn = document.getElementById('copyHtmlBtn');
        const copyReplyHtmlBtn = document.getElementById('copyReplyHtmlBtn');

        copyHtmlBtn?.addEventListener('click', () => this.copyHtml());
        copyReplyHtmlBtn?.addEventListener('click', () => this.copyReplyHtml());

        // Auto-generate on form change (including select dropdown)
        this.form.addEventListener('input', () => {
            if (this.isFormValid()) {
                this.generateSignature();
            }
        });

        this.form.addEventListener('change', () => {
            if (this.isFormValid()) {
                this.generateSignature();
            }
        });
    }

    private getFormData(): SignatureData {
        return {
            firstName: (document.getElementById('firstName') as HTMLInputElement).value.trim(),
            lastName: (document.getElementById('lastName') as HTMLInputElement).value.trim(),
            jobTitle: (document.getElementById('jobTitle') as HTMLInputElement).value.trim(),
            mobileNumber: (document.getElementById('mobileNumber') as HTMLInputElement).value.trim(),
            officeLocation: (document.getElementById('officeLocation') as HTMLSelectElement).value,
            entity: (document.getElementById('entity') as HTMLSelectElement).value
        };
    }

    private isFormValid(): boolean {
        return isFormDataValid(this.getFormData());
    }

    private generateSignature(): void {
        const data = this.getFormData();
        
        if (!isFormDataValid(data)) {
            const missingFields = getMissingFields(data);
            alert(`Please fill in all required fields:\n\n${missingFields.join('\n')}`);
            return;
        }

        // Generate main signature
        const html = createSignatureHtml(data);
        this.previewContainer.innerHTML = html;
        this.previewActions.style.display = 'flex';
        this.previewContainer.dataset.html = html;
        
        // Generate reply signature
        const replyHtml = createReplySignatureHtml(data);
        this.replyPreviewContainer.innerHTML = replyHtml;
        this.replyPreviewActions.style.display = 'flex';
        this.replyPreviewContainer.dataset.html = replyHtml;
    }

    private async copyHtml(): Promise<void> {
        const html = this.previewContainer.dataset.html;
        if (!html) {
            alert('Please generate a signature first');
            return;
        }

        try {
            // Copy as rich HTML content so it pastes formatted in Outlook
            const blob = new Blob([html], { type: 'text/html' });
            const clipboardItem = new ClipboardItem({
                'text/html': blob,
                'text/plain': new Blob([html], { type: 'text/plain' })
            });
            await navigator.clipboard.write([clipboardItem]);
            this.showCopyFeedback('Signature copied! Paste directly into Outlook.');
        } catch (err) {
            // Fallback: copy the rendered content using execCommand
            try {
                const selection = window.getSelection();
                const range = document.createRange();
                range.selectNodeContents(this.previewContainer);
                selection?.removeAllRanges();
                selection?.addRange(range);
                document.execCommand('copy');
                selection?.removeAllRanges();
                this.showCopyFeedback('Signature copied! Paste directly into Outlook.');
            } catch (fallbackErr) {
                // Last resort: copy as plain HTML text
                const textarea = document.createElement('textarea');
                textarea.value = html;
                textarea.style.position = 'fixed';
                textarea.style.opacity = '0';
                document.body.appendChild(textarea);
                textarea.select();
                document.execCommand('copy');
                document.body.removeChild(textarea);
                this.showCopyFeedback('HTML copied to clipboard!');
            }
        }
    }

    private async copyReplyHtml(): Promise<void> {
        const html = this.replyPreviewContainer.dataset.html;
        if (!html) {
            alert('Please generate a signature first');
            return;
        }

        try {
            // Copy as rich HTML content so it pastes formatted in Outlook
            const blob = new Blob([html], { type: 'text/html' });
            const clipboardItem = new ClipboardItem({
                'text/html': blob,
                'text/plain': new Blob([html], { type: 'text/plain' })
            });
            await navigator.clipboard.write([clipboardItem]);
            this.showCopyFeedback('Reply signature copied! Paste directly into Outlook.');
        } catch (err) {
            // Fallback: copy the rendered content using execCommand
            try {
                const selection = window.getSelection();
                const range = document.createRange();
                range.selectNodeContents(this.replyPreviewContainer);
                selection?.removeAllRanges();
                selection?.addRange(range);
                document.execCommand('copy');
                selection?.removeAllRanges();
                this.showCopyFeedback('Reply signature copied! Paste directly into Outlook.');
            } catch (fallbackErr) {
                // Last resort: copy as plain HTML text
                const textarea = document.createElement('textarea');
                textarea.value = html;
                textarea.style.position = 'fixed';
                textarea.style.opacity = '0';
                document.body.appendChild(textarea);
                textarea.select();
                document.execCommand('copy');
                document.body.removeChild(textarea);
                this.showCopyFeedback('Reply HTML copied to clipboard!');
            }
        }
    }

    private showCopyFeedback(message: string): void {
        const feedback = document.createElement('div');
        feedback.textContent = message;
        feedback.style.cssText = 'position: fixed; top: 20px; right: 20px; background: #4CAF50; color: white; padding: 12px 24px; border-radius: 4px; box-shadow: 0 2px 8px rgba(0,0,0,0.2); z-index: 1000;';
        document.body.appendChild(feedback);
        
        setTimeout(() => {
            feedback.remove();
        }, 2000);
    }

}

// Initialize the application when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
    new SignatureGenerator();
});
